import { makeAutoObservable, runInAction } from 'mobx';
import type { BidDetails } from '../services/bidAnalysis/bidAnalysisServices';
import * as bidAnalysisServicesModule from '../services/bidAnalysis/bidAnalysisServices';
import { authServices } from '../services/authServices';
import type { MarketSalesTrendItem } from '../screens/BidAnalysis/BidAnalysisTemplate/shared/types';

const bidAnalysisServices = bidAnalysisServicesModule.bidAnalysisServices;

// Re-export types from services to maintain consistency
export type OpportunityIteration = bidAnalysisServicesModule.OpportunityIteration;
export type { BidDetails };

/** Shape returned by getActiveBids(); store assigns the full response to activeBids */
export type ActiveBidsResponse = {
  data: bidAnalysisServicesModule.BidAnalysisProject[];
  count: number;
  total_pages?: number;
};

// Chart data storage key format: `${bidId}_${concept}_${packSize}_${labeller || 'all'}`
interface _ChartDataKey {
  bidId: number;
  concept: string;
  packSize: number;
  labeller?: string | null;
}

export class BidAnalysisStore {
  // Wizard state
  selectedContractType: 'mas' | 'nc' | null = null;
  selectedOpportunity: OpportunityIteration | null = null;
  selectedProductType: 'direct' | 'indirect' | null = null;
  opportunityIterations: OpportunityIteration[] = [];
  opportunityIterationsContractType: 'mas' | 'nc' | null = null; // Track which contract type the opportunities are for
  
  // Active bids (full paginated response from getActiveBids)
  activeBids: ActiveBidsResponse = { data: [], count: 0 };
  
  // Current bid details
  currentBidDetails: BidDetails | null = null;
  currentBidId: number | null = null;
  
  // Chart data storage - key: `${bidId}_${concept}_${packSize}_${labeller || 'all'}`
  marketSalesTrendData: Map<string, MarketSalesTrendItem[]> = new Map();
  marketShareByVolumeData: Map<string, Array<{ vendor: string; share: number }>> = new Map();
  
  // Contract price ranking storage - key: `${bidId}_${concept}_${packSize}_${startDate}_${endDate}`
  contractPriceRankingData: Map<string, Array<any>> = new Map();
  
  // Chart date range storage - key: `${bidId}_${concept}_${packSize}`
  chartDateRanges: Map<string, { startDate: Date | null; endDate: Date | null }> = new Map();
  
  // NDC options storage - key: `${supplier}_${concept}_${packSize}`
  ndcOptionsData: Map<string, Array<{ ndc11: string; brand_name: string; pack_size: number; labeler: string; concept_name: string }>> = new Map();
  
  // Supplier discount cache - key: supplier_code (first 5 chars of NDC)
  supplierDiscountCache: Map<string, number> = new Map();
  
  // Reference Tables - Attributes and User-Attribute Mappings
  attributes: bidAnalysisServicesModule.Attribute[] = [];
  userMappings: bidAnalysisServicesModule.UserAttributeMappingDisplay[] = [];
  attributesWithUsers: bidAnalysisServicesModule.AttributeWithUsers[] = [];
  usersList: any[] = [];
  
  // Reference Tables - Assignment Rules
  assignmentRules: bidAnalysisServicesModule.AssignmentRule[] = [];
  
  // Reference Tables - Supplier Discounts
  supplierDiscounts: bidAnalysisServicesModule.SupplierDiscount[] = [];
  
  // Loading states
  isLoadingOpportunities = false;
  isCreatingBid = false;
  isLoadingBidDetails = false;
  isLoadingActiveBids = false;
  isLoadingSupplierDiscount = false;
  isLoadingAttributes = false;
  isLoadingUserMappings = false;
  isLoadingUsersList = false;
  isLoadingRules = false;
  isLoadingSupplierDiscounts = false;
  
  // Error states
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Reset wizard state
  resetWizard() {
    this.selectedContractType = null;
    this.selectedOpportunity = null;
    this.selectedProductType = null;
    this.opportunityIterations = [];
    this.opportunityIterationsContractType = null;
    this.error = null;
  }

  // Clear error state
  clearError() {
    this.error = null;
  }

  // Set selected contract type
  setContractType(type: 'mas' | 'nc') {
    this.selectedContractType = type;
  }

  // Fetch opportunity iterations based on contract type
  async fetchOpportunityIterations(opportunityType: 'mas' | 'nc') {
    // If we already have opportunities for this contract type, don't fetch again
    if (this.opportunityIterationsContractType === opportunityType && this.opportunityIterations.length > 0) {
      return;
    }

    this.isLoadingOpportunities = true;
    this.error = null;

    try {
      const data = await bidAnalysisServices.getOpportunityIterations(opportunityType);
      
      runInAction(() => {
        // Ensure we always set an array, even if API returns something unexpected
        this.opportunityIterations = Array.isArray(data) ? data : [];
        this.opportunityIterationsContractType = opportunityType; // Track which contract type these opportunities are for
        this.isLoadingOpportunities = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch opportunities';
        this.opportunityIterations = []; // Reset to empty array on error
        this.opportunityIterationsContractType = null;
        this.isLoadingOpportunities = false;
      });
      throw error;
    }
  }

  // Set selected opportunity
  setSelectedOpportunity(opportunity: OpportunityIteration) {
    this.selectedOpportunity = opportunity;
  }

  // Set selected product type
  setProductType(type: 'direct' | 'indirect') {
    this.selectedProductType = type;
  }

  // Create new bid
  async createBid() {
    // For NC template, product_type is not required
    const isNC = this.selectedContractType === 'nc';
    if (!this.selectedContractType || !this.selectedOpportunity || (!isNC && !this.selectedProductType)) {
      throw new Error('Missing required data for bid creation');
    }

    this.isCreatingBid = true;
    this.error = null;

    try {
      const payload: bidAnalysisServicesModule.CreateBidRequest = {
        template_type: this.selectedContractType,
        opportunity_iteration_id: this.selectedOpportunity.id,
      };
      
      // Only include product_type if it's selected (required for MAS, optional for NC)
      if (this.selectedProductType) {
        payload.product_type = this.selectedProductType;
      }
      
      const response = await bidAnalysisServices.createBid(payload);

      runInAction(() => {
        this.currentBidId = response.bid_id;
        this.isCreatingBid = false;
      });

      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to create bid';
        this.isCreatingBid = false;
      });
      throw error;
    }
  }

  // Fetch bid details
  async fetchBidDetails(bidId: number) {
    this.isLoadingBidDetails = true;
    this.error = null;

    try {
      const data = await bidAnalysisServices.getBidDetails(bidId);
      
      runInAction(() => {
        this.currentBidDetails = data;
        this.currentBidId = bidId;
        this.isLoadingBidDetails = false;
      });

      return data;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch bid details';
        this.isLoadingBidDetails = false;
      });
      throw error;
    }
  }

  // Fetch all active bids
  async fetchActiveBids() {
    this.isLoadingActiveBids = true;
    this.error = null;

    try {
      const data = await bidAnalysisServices.getActiveBids();
      
      runInAction(() => {
        this.activeBids = data || { data: [], count: 0 };
        this.isLoadingActiveBids = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch active bids';
        this.isLoadingActiveBids = false;
      });
      throw error;
    }
  }

  // Clear current bid
  clearCurrentBid() {
    this.currentBidDetails = null;
    this.currentBidId = null;
  }

  // Get product type from current bid
  get productType(): 'direct' | 'indirect' | null {
    return this.currentBidDetails?.product_type || null;
  }

  // Get template type from current bid
  get templateType(): 'mas' | 'nc' | null {
    return this.currentBidDetails?.template_type || null;
  }

  // Generate chart data key
  private getChartDataKey(bidId: number, concept: string, packSize: number, labeller?: string | null): string {
    return `${bidId}_${concept}_${packSize}_${labeller || 'all'}`;
  }

  // Store market sales trend data
  setMarketSalesTrendData(bidId: number, concept: string, packSize: number, labeller: string | null | undefined, data: MarketSalesTrendItem[]) {
    const key = this.getChartDataKey(bidId, concept, packSize, labeller);
    this.marketSalesTrendData.set(key, data);
  }

  // Get market sales trend data
  getMarketSalesTrendData(bidId: number, concept: string, packSize: number, labeller?: string | null): MarketSalesTrendItem[] | undefined {
    const key = this.getChartDataKey(bidId, concept, packSize, labeller);
    return this.marketSalesTrendData.get(key);
  }

  // Store market share by volume data
  setMarketShareByVolumeData(bidId: number, concept: string, packSize: number, data: Array<{ vendor: string; share: number }>) {
    const key = this.getChartDataKey(bidId, concept, packSize, null);
    this.marketShareByVolumeData.set(key, data);
  }

  // Get market share by volume data
  getMarketShareByVolumeData(bidId: number, concept: string, packSize: number): Array<{ vendor: string; share: number }> | undefined {
    const key = this.getChartDataKey(bidId, concept, packSize, null);
    return this.marketShareByVolumeData.get(key);
  }

  // Generate contract price ranking key
  private getContractPriceRankingKey(bidId: number, concept: string, packSize: number, startDate?: string, endDate?: string): string {
    const dateKey = startDate && endDate ? `_${startDate}_${endDate}` : '';
    return `${bidId}_${concept}_${packSize}${dateKey}`;
  }

  // Store contract price ranking data
  setContractPriceRankingData(bidId: number, concept: string, packSize: number, data: Array<any>, startDate?: string, endDate?: string) {
    const key = this.getContractPriceRankingKey(bidId, concept, packSize, startDate, endDate);
    this.contractPriceRankingData.set(key, data);
  }

  // Get contract price ranking data
  getContractPriceRankingData(bidId: number, concept: string, packSize: number, startDate?: string, endDate?: string): Array<any> | undefined {
    const key = this.getContractPriceRankingKey(bidId, concept, packSize, startDate, endDate);
    return this.contractPriceRankingData.get(key);
  }

  // Generate chart date range key
  private getChartDateRangeKey(bidId: number, concept: string, packSize: number): string {
    return `${bidId}_${concept}_${packSize}`;
  }

  // Store chart date range (the "applied" filter)
  setChartDateRange(bidId: number, concept: string, packSize: number, dateRange: { startDate: Date | null; endDate: Date | null }) {
    const key = this.getChartDateRangeKey(bidId, concept, packSize);
     this.chartDateRanges.set(key, dateRange);
  }

  // Get chart date range (the "applied" filter)
  getChartDateRange(bidId: number, concept: string, packSize: number): { startDate: Date | null; endDate: Date | null } | undefined {
    const key = this.getChartDateRangeKey(bidId, concept, packSize);
    return this.chartDateRanges.get(key);
  }

  // Generate NDC options key
  private getNdcOptionsKey(supplier: string, concept: string, packSize: number): string {
    return `${supplier}_${concept}_${packSize}`;
  }

  // Store NDC options
  setNdcOptions(supplier: string, concept: string, packSize: number, data: Array<{ ndc11: string; brand_name: string; pack_size: number; labeler: string; concept_name: string }>) {
    const key = this.getNdcOptionsKey(supplier, concept, packSize);
    this.ndcOptionsData.set(key, data);
  }

  // Get NDC options
  getNdcOptions(supplier: string, concept: string, packSize: number): Array<{ ndc11: string; brand_name: string; pack_size: number; labeler: string; concept_name: string }> | undefined {
    const key = this.getNdcOptionsKey(supplier, concept, packSize);
    return this.ndcOptionsData.get(key);
  }

  // Get supplier discount from cache
  getSupplierDiscount(supplierCode: string): number | undefined {
    return this.supplierDiscountCache.get(supplierCode);
  }

  // Fetch and cache supplier discount
  async fetchSupplierDiscount(supplierCode: string): Promise<number | null> {
    // Check cache first
    const cached = this.supplierDiscountCache.get(supplierCode);
    if (cached !== undefined) {
      return cached;
    }

    this.isLoadingSupplierDiscount = true;
    this.error = null;

    try {
      const discountData = await bidAnalysisServices.getSupplierDiscount(supplierCode);
      const totalDiscount = discountData.total_discount !== null && discountData.total_discount !== undefined 
        ? discountData.total_discount 
        : 0;

      runInAction(() => {
        this.supplierDiscountCache.set(supplierCode, totalDiscount);
        this.isLoadingSupplierDiscount = false;
      });

      return totalDiscount;
    } catch (error: any) {
      // If 404, cache null to indicate no discount found
      if (error?.is404 || error?.response?.status === 404) {
        runInAction(() => {
          this.supplierDiscountCache.set(supplierCode, 0); // Store 0 for no discount
          this.isLoadingSupplierDiscount = false;
        });
        return 0;
      }

      runInAction(() => {
        this.error = error.message || 'Failed to fetch supplier discount';
        this.isLoadingSupplierDiscount = false;
      });
      throw error;
    }
  }

  // Clear supplier discount cache (useful when switching bids or resetting)
  clearSupplierDiscountCache() {
    this.supplierDiscountCache.clear();
  }

  // Clear chart data for a specific bid (useful when switching bids)
  clearChartData(bidId?: number) {
    if (bidId) {
      // Clear only data for this bid
      const keysToDelete: string[] = [];
      this.marketSalesTrendData.forEach((_, key) => {
        if (key.startsWith(`${bidId}_`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.marketSalesTrendData.delete(key));

      keysToDelete.length = 0;
      this.marketShareByVolumeData.forEach((_, key) => {
        if (key.startsWith(`${bidId}_`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.marketShareByVolumeData.delete(key));

      // Clear contract price ranking data for this bid
      keysToDelete.length = 0;
      this.contractPriceRankingData.forEach((_, key) => {
        if (key.startsWith(`${bidId}_`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.contractPriceRankingData.delete(key));

      // Clear chart date ranges for this bid
      keysToDelete.length = 0;
      this.chartDateRanges.forEach((_, key) => {
        if (key.startsWith(`${bidId}_`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.chartDateRanges.delete(key));
    } else {
      // Clear all chart data
      this.marketSalesTrendData.clear();
      this.marketShareByVolumeData.clear();
      this.contractPriceRankingData.clear();
      this.chartDateRanges.clear();
    }
  }

  // ============================================================
  // REFERENCE TABLES - ATTRIBUTES
  // ============================================================

  // Fetch all attributes
  async fetchAttributes(forceRefresh: boolean = false) {
    // If we already have attributes and not forcing refresh, don't fetch again
    if (!forceRefresh && this.attributes.length > 0) {
      return;
    }

    this.isLoadingAttributes = true;
    this.error = null;

    try {
      const data = await bidAnalysisServices.getAllAttributes();
      
      runInAction(() => {
        this.attributes = Array.isArray(data) ? data : [];
        this.isLoadingAttributes = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch attributes';
        this.attributes = [];
        this.isLoadingAttributes = false;
      });
      throw error;
    }
  }

  // Create attribute
  async createAttribute(payload: bidAnalysisServicesModule.CreateAttributeRequest) {
    this.isLoadingAttributes = true;
    this.error = null;

    try {
      const data = await bidAnalysisServices.createAttribute(payload);
      
      runInAction(() => {
        this.attributes.push(data);
        this.isLoadingAttributes = false;
      });

      return data;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to create attribute';
        this.isLoadingAttributes = false;
      });
      throw error;
    }
  }

  // Update attribute
  async updateAttribute(attributeId: number, payload: bidAnalysisServicesModule.UpdateAttributeRequest) {
    this.isLoadingAttributes = true;
    this.error = null;

    try {
      const data = await bidAnalysisServices.updateAttribute(attributeId, payload);
      
      runInAction(() => {
        const index = this.attributes.findIndex(a => a.id === attributeId);
        if (index !== -1) {
          this.attributes[index] = data;
        }
        this.isLoadingAttributes = false;
      });

      return data;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to update attribute';
        this.isLoadingAttributes = false;
      });
      throw error;
    }
  }

  // Delete attribute
  async deleteAttribute(attributeId: number) {
    this.isLoadingAttributes = true;
    this.error = null;

    try {
      await bidAnalysisServices.deleteAttribute(attributeId);
      
      runInAction(() => {
        this.attributes = this.attributes.filter(a => a.id !== attributeId);
        // Also remove related user mappings
        this.userMappings = this.userMappings.filter(m => m.attribute_id !== attributeId);
        this.isLoadingAttributes = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to delete attribute';
        this.isLoadingAttributes = false;
      });
      throw error;
    }
  }

  // ============================================================
  // REFERENCE TABLES - USER-ATTRIBUTE MAPPINGS
  // ============================================================

  // Fetch user mappings
  async fetchUserMappings(forceRefresh: boolean = false) {
    // If we already have mappings and not forcing refresh, don't fetch again
    if (!forceRefresh && this.attributesWithUsers.length > 0) {
      return;
    }

    this.isLoadingUserMappings = true;
    this.error = null;

    try {
      // Use the new API endpoint that returns attributes with users
      const attributesWithUsers = await bidAnalysisServices.getAttributesWithUsers();

      // Also maintain flat list for backward compatibility if needed
      const allMappings: bidAnalysisServicesModule.UserAttributeMappingDisplay[] = [];

      for (const attribute of attributesWithUsers) {
        for (const user of attribute.users) {
          allMappings.push({
            mapping_id: user.mapping_id,
            attribute_id: attribute.id,
            attribute_name: attribute.attribute_name,
            user_id: user.user_id,
            user_name: user.user_details.name,
            user_email: user.user_details.email,
            department_name: user.user_details.department_name,
            last_modified_by: user.last_modified_by,
            last_modified_date: user.last_modified_date,
          });
        }
      }

      runInAction(() => {
        this.attributesWithUsers = attributesWithUsers;
        this.userMappings = allMappings;
        this.isLoadingUserMappings = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch user mappings';
        this.attributesWithUsers = [];
        this.userMappings = [];
        this.isLoadingUserMappings = false;
      });
      throw error;
    }
  }

  // Add users to attribute
  async addUsersToAttribute(attributeId: number, payload: bidAnalysisServicesModule.AddUsersToAttributeRequest) {
    this.isLoadingUserMappings = true;
    this.error = null;

    try {
      const response = await bidAnalysisServices.addUsersToAttribute(attributeId, payload);
      
      // Refresh mappings after adding - force refresh
      await this.fetchUserMappings(true);
      
      runInAction(() => {
        this.isLoadingUserMappings = false;
      });

      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to add users to attribute';
        this.isLoadingUserMappings = false;
      });
      throw error;
    }
  }

  // Remove users from attribute
  async removeUsersFromAttribute(attributeId: number, payload: bidAnalysisServicesModule.AddUsersToAttributeRequest) {
    this.isLoadingUserMappings = true;
    this.error = null;

    try {
      await bidAnalysisServices.removeUsersFromAttribute(attributeId, payload);
      
      // Refresh mappings after removing
      await this.fetchUserMappings(true);
      
      runInAction(() => {
        this.isLoadingUserMappings = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to remove users from attribute';
        this.isLoadingUserMappings = false;
      });
      throw error;
    }
  }

  // Fetch users list
  async fetchUsersList() {
    // If we already have users, don't fetch again
    if (this.usersList.length > 0) {
      return;
    }

    this.isLoadingUsersList = true;
    this.error = null;

    try {
      const response = await authServices.getUsersList();
      
      runInAction(() => {
        this.usersList = response.users || [];
        this.isLoadingUsersList = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch users list';
        this.usersList = [];
        this.isLoadingUsersList = false;
      });
      throw error;
    }
  }

  // ============================================================
  // REFERENCE TABLES - ASSIGNMENT RULES
  // ============================================================

  // Fetch all assignment rules
  async fetchAssignmentRules(forceRefresh: boolean = false) {
    // If we already have rules and not forcing refresh, don't fetch again
    if (!forceRefresh && this.assignmentRules.length > 0) {
      return;
    }

    this.isLoadingRules = true;
    this.error = null;

    try {
      const data = await bidAnalysisServices.getAllRules();
      
      runInAction(() => {
        this.assignmentRules = Array.isArray(data) ? data : [];
        this.isLoadingRules = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch assignment rules';
        this.assignmentRules = [];
        this.isLoadingRules = false;
      });
      throw error;
    }
  }

  // Create assignment rule
  async createAssignmentRule(payload: bidAnalysisServicesModule.CreateRuleRequest) {
    this.isLoadingRules = true;
    this.error = null;

    try {
      const data = await bidAnalysisServices.createRule(payload);
      
      runInAction(() => {
        this.assignmentRules.push(data);
        this.isLoadingRules = false;
      });

      return data;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to create assignment rule';
        this.isLoadingRules = false;
      });
      throw error;
    }
  }

  // Update assignment rule
  async updateAssignmentRule(ruleId: number, payload: bidAnalysisServicesModule.UpdateRuleRequest) {
    this.isLoadingRules = true;
    this.error = null;

    try {
      const data = await bidAnalysisServices.updateRule(ruleId, payload);
      
      runInAction(() => {
        const index = this.assignmentRules.findIndex(r => r.id === ruleId);
        if (index !== -1) {
          this.assignmentRules[index] = data;
        }
        this.isLoadingRules = false;
      });

      return data;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to update assignment rule';
        this.isLoadingRules = false;
      });
      throw error;
    }
  }

  // Delete assignment rule
  async deleteAssignmentRule(ruleId: number) {
    this.isLoadingRules = true;
    this.error = null;

    try {
      await bidAnalysisServices.deleteRule(ruleId);
      
      runInAction(() => {
        this.assignmentRules = this.assignmentRules.filter(r => r.id !== ruleId);
        this.isLoadingRules = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to delete assignment rule';
        this.isLoadingRules = false;
      });
      throw error;
    }
  }

  // ============================================================
  // REFERENCE TABLES - SUPPLIER DISCOUNTS
  // ============================================================

  // Fetch all supplier discounts
  async fetchSupplierDiscounts(forceRefresh: boolean = false) {
    if (!forceRefresh && this.supplierDiscounts.length > 0) {
      return;
    }

    this.isLoadingSupplierDiscounts = true;
    this.error = null;

    try {
      const data = await bidAnalysisServices.getAllSupplierDiscounts();

      runInAction(() => {
        this.supplierDiscounts = Array.isArray(data) ? data : [];
        this.isLoadingSupplierDiscounts = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch supplier discounts';
        this.supplierDiscounts = [];
        this.isLoadingSupplierDiscounts = false;
      });
      throw error;
    }
  }

  // Create supplier discount
  async createSupplierDiscount(payload: bidAnalysisServicesModule.CreateSupplierDiscountRequest) {
    this.isLoadingSupplierDiscounts = true;
    this.error = null;

    try {
      const data = await bidAnalysisServices.createSupplierDiscount(payload);
      await this.fetchSupplierDiscounts(true);
      return data;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to create supplier discount';
        this.isLoadingSupplierDiscounts = false;
      });
      throw error;
    }
  }

  // Update supplier discount
  async updateSupplierDiscount(discountId: number, payload: bidAnalysisServicesModule.UpdateSupplierDiscountRequest) {
    this.isLoadingSupplierDiscounts = true;
    this.error = null;

    try {
      const data = await bidAnalysisServices.updateSupplierDiscount(discountId, payload);
      await this.fetchSupplierDiscounts(true);
      return data;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to update supplier discount';
        this.isLoadingSupplierDiscounts = false;
      });
      throw error;
    }
  }

  // Delete supplier discount
  async deleteSupplierDiscount(discountId: number) {
    this.isLoadingSupplierDiscounts = true;
    this.error = null;

    try {
      await bidAnalysisServices.deleteSupplierDiscount(discountId);
      
      runInAction(() => {
        this.supplierDiscounts = this.supplierDiscounts.filter(d => d.id !== discountId);
        this.isLoadingSupplierDiscounts = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to delete supplier discount';
        this.isLoadingSupplierDiscounts = false;
      });
      throw error;
    }
  }
}

export const bidAnalysisStore = new BidAnalysisStore();

